import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { CASE_STUDIES } from '../data/caseStudies';
import type { CaseStudy } from '../types';
import { ArrowRight, Trophy, Zap } from 'lucide-react';
import { MiniCaseStudies } from '../components/MiniCaseStudies';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

const CaseStudySection: React.FC<{ caseStudy: CaseStudy, index: number }> = ({ caseStudy, index }) => (
    <RevealOnScroll delay={index * 100}>
        <div id={caseStudy.id} className="scroll-mt-20">
            <GlassCard variant="medium" className="overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div className={`relative ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                        <GlassCard variant="subtle" className="p-0 overflow-hidden">
                            <img
                                src={caseStudy.imageUrl}
                                alt={`Case study dla klienta ${caseStudy.clientName}`}
                                className="w-full h-auto object-cover aspect-[4/3]"
                                loading="lazy"
                            />
                        </GlassCard>
                    </div>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
                            Case Study: {caseStudy.clientName}
                        </h2>
                        <p className="text-lg font-semibold text-primary-light dark:text-primary-dark mb-6">
                            Rozwiązanie: <Link to={caseStudy.solution.path} className="hover:underline">{caseStudy.solution.name}</Link>
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {caseStudy.results.map(result => (
                                <GlassCard key={result.description} variant="subtle" className="text-center p-4">
                                    <div className="text-primary-light dark:text-primary-dark mb-2">
                                        {result.icon}
                                    </div>
                                    <p className="text-xl md:text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{result.value}</p>
                                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{result.description}</p>
                                </GlassCard>
                            ))}
                        </div>

                        <div className="prose dark:prose-invert max-w-none text-text-secondary-light dark:text-text-secondary-dark text-sm">
                            {caseStudy.content}
                        </div>
                    </div>
                </div>
            </GlassCard>
        </div>
    </RevealOnScroll>
);

export const CaseStudiesPage: React.FC = () => {
    usePageMetadata({
        title: "Case Studies: Wyniki Wdrożeń AI | BiznesBezKlikania.pl",
        description: "Zobacz, jak pomogliśmy firmom e-commerce, prawnym i B2B osiągnąć realne wyniki dzięki AI. Poznaj konkretne liczby i historie sukcesu naszych klientów.",
        schema: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Case Studies – Realne Wyniki Automatyzacji AI",
            "description": "Zbiór studiów przypadku pokazujących efektywność wdrożeń AI w różnych branżach.",
            "url": "https://biznesbezklikania.pl/case-studies"
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Trophy className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Historie Sukcesu
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            Zobacz, jak realnie pomagamy firmom oszczędzać czas i zwiększać zyski dzięki automatyzacji AI. Zero teorii, same konkretne wyniki.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Mini Case Studies */}
            <section className="py-16 relative bg-content-light/50 dark:bg-content-dark/50">
                <MiniCaseStudies />
            </section>

            {/* Detailed Case Studies */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Szczegółowe Wdrożenia
                        </h2>
                    </RevealOnScroll>

                    <div className="space-y-8">
                        {CASE_STUDIES.map((cs, index) => (
                            <CaseStudySection key={cs.id} caseStudy={cs} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 relative overflow-hidden">
                <AnimatedBlob variant="purple" />
                <ParticlesBackground variant="minimal" />
                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                            Chcesz osiągnąć podobne rezultaty?
                        </h2>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto mb-8">
                            Porozmawiajmy o tym, jak możemy wdrożyć dedykowane rozwiązania AI w Twojej firmie. Konsultacja jest bezpłatna.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <Link
                            to="/kontakt"
                            className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-2xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center gap-2 animate-glow"
                        >
                            <Zap className="w-5 h-5" />
                            Umów bezpłatną konsultację
                        </Link>
                    </RevealOnScroll>
                </div>
            </section>
        </>
    );
};
